import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plane, Activity, Globe, Gauge, Newspaper, RefreshCw } from 'lucide-react';

import { BACKEND_URL, API } from '../config';

// Datos en Vivo: feeds públicos del mundo real que la plataforma guarda en BD
// (colección live_data) para que la IA aprenda del histórico.
const LiveData = () => {
  const [flights, setFlights] = useState(null);
  const [quakes, setQuakes] = useState(null);
  const [market, setMarket] = useState(null);
  const [fg, setFg] = useState(null);
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    const opts = { withCredentials: true };
    const safe = async (fn) => { try { return await fn(); } catch { return null; } };
    const [f, q, m, g, n] = await Promise.all([
      safe(() => axios.get(`${API}/livedata/flights`, opts)),
      safe(() => axios.get(`${API}/livedata/earthquakes`, opts)),
      safe(() => axios.get(`${API}/livedata/global-market`, opts)),
      safe(() => axios.get(`${API}/livedata/fear-greed`, opts)),
      safe(() => axios.get(`${API}/market/news?limit=8`, opts)),
    ]);
    setFlights(f?.data || null);
    setQuakes(q?.data || null);
    setMarket(m?.data || null);
    setFg(g?.data || null);
    setNews(n?.data?.news || n?.data || null);
    setUpdatedAt(new Date().toLocaleTimeString());
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60000); // refresco cada minuto
    return () => clearInterval(id);
  }, []);

  const fmtUsd = (v) => v ? `$${(v / 1e12 >= 1 ? (v / 1e12).toFixed(2) + ' T' : (v / 1e9).toFixed(1) + ' B')}` : '—';

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Datos en Vivo</h1>
            <p className="text-[#A1A1AA] text-sm">
              Feeds del mundo real (aviones, sismos, mercado, sentimiento y noticias).
              Cada lectura se guarda para que la IA Aegis aprenda del contexto global.
              {updatedAt && ` · Actualizado ${updatedAt}`}
            </p>
          </div>
          <button onClick={fetchAll} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded bg-[#00D4FF] text-black text-sm font-medium">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mercado global */}
          <Card className="bg-[#1E1E1E] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2">
              <Globe size={18} className="text-[#00D4FF]" /> Mercado Cripto Global</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              {market ? (<>
                <p className="text-[#A1A1AA]">Capitalización: <span className="text-white">{fmtUsd(market.market_cap_usd)}</span>
                  {market.cambio_24h_pct != null && (
                    <span className={market.cambio_24h_pct >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {' '}({market.cambio_24h_pct >= 0 ? '+' : ''}{market.cambio_24h_pct?.toFixed(2)}% 24h)</span>)}
                </p>
                <p className="text-[#A1A1AA]">Volumen 24h: <span className="text-white">{fmtUsd(market.volumen_24h_usd)}</span></p>
                <p className="text-[#A1A1AA]">Dominancia BTC: <span className="text-white">{market.dominancia_btc?.toFixed(1)}%</span></p>
                <p className="text-[#A1A1AA]">Criptomonedas activas: <span className="text-white">{market.criptos_activas?.toLocaleString()}</span></p>
              </>) : <p className="text-[#A1A1AA]">{loading ? 'Cargando…' : 'No disponible'}</p>}
            </CardContent>
          </Card>

          {/* Fear & Greed */}
          <Card className="bg-[#1E1E1E] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2">
              <Gauge size={18} className="text-[#f08c1a]" /> Fear & Greed (la IA lo usa en sus predicciones)</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {fg?.actual ? (<>
                <p className="text-3xl font-bold" style={{ color: fg.actual.valor < 35 ? '#d8493f' : fg.actual.valor > 65 ? '#0eaa6b' : '#f08c1a' }}>
                  {fg.actual.valor}/100</p>
                <p className="text-[#A1A1AA]">{fg.actual.clasificacion}</p>
                <div className="flex gap-1 mt-3">
                  {fg.ultimos_7_dias?.slice().reverse().map((d, i) => (
                    <div key={i} title={`${d.valor}`} className="flex-1 rounded"
                      style={{ height: 36, background: d.valor < 35 ? '#d8493f' : d.valor > 65 ? '#0eaa6b' : '#f08c1a', opacity: 0.35 + d.valor / 160 }} />
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-xs mt-1">Últimos 7 días</p>
              </>) : <p className="text-[#A1A1AA]">{loading ? 'Cargando…' : 'No disponible'}</p>}
            </CardContent>
          </Card>

          {/* Aviones */}
          <Card className="bg-[#1E1E1E] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2">
              <Plane size={18} className="text-[#00FF88]" /> Aviones en vivo (OpenSky · Latinoamérica)</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {flights ? (<>
                <p className="text-[#A1A1AA] mb-2">En el aire ahora: <span className="text-white font-medium">{flights.total?.toLocaleString()}</span></p>
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {flights.flights?.slice(0, 12).map((f, i) => (
                    <div key={i} className="flex justify-between text-xs border-b border-[#27272A] py-1">
                      <span className="text-white">{f.callsign || 'N/D'} <span className="text-[#A1A1AA]">({f.pais})</span></span>
                      <span className="text-[#A1A1AA]">{f.alt_m ? `${Math.round(f.alt_m)} m` : 'tierra'} · {f.vel_ms ? `${Math.round(f.vel_ms * 3.6)} km/h` : '—'}</span>
                    </div>))}
                </div>
              </>) : <p className="text-[#A1A1AA]">{loading ? 'Cargando…' : 'OpenSky saturado, reintenta en unos segundos'}</p>}
            </CardContent>
          </Card>

          {/* Sismos */}
          <Card className="bg-[#1E1E1E] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2">
              <Activity size={18} className="text-[#d8493f]" /> Sismos del día (USGS, M ≥ 2.5)</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {quakes ? (
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {quakes.earthquakes?.slice(0, 12).map((q, i) => (
                    <div key={i} className="flex justify-between text-xs border-b border-[#27272A] py-1">
                      <span className="text-white truncate pr-2">{q.lugar}</span>
                      <span className={q.magnitud >= 5 ? 'text-red-400 font-bold' : 'text-[#f08c1a]'}>M {q.magnitud?.toFixed(1)}</span>
                    </div>))}
                </div>
              ) : <p className="text-[#A1A1AA]">{loading ? 'Cargando…' : 'No disponible'}</p>}
            </CardContent>
          </Card>
        </div>

        {/* Noticias */}
        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2">
            <Newspaper size={18} className="text-[#00D4FF]" /> Noticias en vivo (alimentan el sentimiento de la IA)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {Array.isArray(news) && news.length ? news.slice(0, 8).map((n, i) => (
              <a key={i} href={n.url} target="_blank" rel="noreferrer"
                className="block border-b border-[#27272A] pb-2 hover:opacity-80">
                <p className="text-white">{n.title}</p>
                <p className="text-[#A1A1AA] text-xs">{n.source || n.site || ''}</p>
              </a>
            )) : <p className="text-[#A1A1AA]">{loading ? 'Cargando…' : 'Ver pestaña News para el feed completo'}</p>}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LiveData;
